// Unified Review Workflow Integration — Dashboard Reviews_Due_Queue surface.
//
// Renders the vCIO+ work-queue of Review_Cycles that are due for a unified
// pass. The component is intentionally thin: all genuine logic lives in pure
// selectors (`@/lib/reviewWorkflow/due`, `@/lib/reviewWorkflow/routing`,
// `@/lib/reviewWorkflow/errors`) and in the org-scoped read hook
// (`@/hooks/useReviewsDue`). This file only maps the hook's distinct result
// states onto distinct presentations and wires per-item navigation.
//
// Behavior (Req 2.3–2.7):
//   - Loading: an explicit in-progress state, distinct from empty and error
//     (Req 2.4).
//   - Empty: a successful response with no due cycles renders an explicit empty
//     state, distinct from loading and error (Req 2.5).
//   - Error: a generic, internal-detail-free message via `toUserFacingError`
//     plus a retry affordance that re-issues the SAME org-scoped request via
//     the hook's `refetch` (Req 2.6).
//   - Loaded: each due cycle shows its customer name and `dueDate` and a control
//     navigating to `unifiedRouteForCycle(cycle.id)` (Req 2.3). Ordering is
//     applied server-side and re-asserted here via `sortDueCycles` for
//     deterministic display (Req 2.2).
//   - The queue presents NO control that initiates a unified pass or cycle
//     completion (Req 2.7) — it only navigates to the unified review page.
//
// References: design.md "ReviewsDueQueue component", requirements 2.3–2.7.

import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { useReviewsDue } from "@/hooks/useReviewsDue";
import { sortDueCycles } from "@/lib/reviewWorkflow/due";
import { unifiedRouteForCycle } from "@/lib/reviewWorkflow/routing";
import { toUserFacingError } from "@/lib/reviewWorkflow/errors";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FormattedDate } from "@/components/ui/formatted-date";
import { cn } from "@/lib/utils";
import { AlertCircle, CalendarClock, ChevronRight, Inbox } from "lucide-react";

/**
 * Describes how overdue a due cycle is, for an at-a-glance urgency cue. The
 * Reviews_Due_Queue only ever contains cycles whose dueDate is on or before
 * now (see `isCycleDue`), so this is always "due today" or overdue by some
 * amount. Severity escalates the badge/icon colour so the most overdue rows
 * (which already sort first) also read as the most urgent.
 */
function describeUrgency(dueDate: string): {
  label: string;
  badgeClass: string;
  iconWrapClass: string;
} {
  const due = new Date(dueDate).getTime();
  const neutral = {
    label: "Due today",
    badgeClass:
      "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
    iconWrapClass:
      "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  };
  if (Number.isNaN(due)) return neutral;

  const dayMs = 1000 * 60 * 60 * 24;
  const daysOverdue = Math.floor((Date.now() - due) / dayMs);

  if (daysOverdue <= 0) return neutral;

  if (daysOverdue < 31) {
    return {
      label: `${daysOverdue} ${daysOverdue === 1 ? "day" : "days"} overdue`,
      badgeClass:
        "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
      iconWrapClass:
        "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    };
  }

  const monthsOverdue = Math.round(daysOverdue / 30);
  return {
    label: `${monthsOverdue} ${monthsOverdue === 1 ? "month" : "months"} overdue`,
    badgeClass:
      "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400",
    iconWrapClass:
      "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400",
  };
}

function QueueShell({ children }: { children: ReactNode }) {
  return (
    <Card data-testid="reviews-due-queue">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          Reviews Due
        </CardTitle>
        <CardDescription>
          Review cycles due for a unified pass, most overdue first.
        </CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function ReviewsDueQueue() {
  const [, setLocation] = useLocation();
  const { data, isLoading, isError, error, refetch, isFetching } =
    useReviewsDue();

  // ---- Loading state (Req 2.4) ------------------------------------------
  if (isLoading) {
    return (
      <QueueShell>
        <div
          className="space-y-3"
          role="status"
          aria-live="polite"
          aria-busy="true"
          data-testid="reviews-due-loading"
        >
          <span className="sr-only">Loading reviews due…</span>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-lg border p-3"
            >
              <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>
          ))}
        </div>
      </QueueShell>
    );
  }

  // ---- Error state with retry (Req 2.6) ---------------------------------
  if (isError) {
    const { message } = toUserFacingError(error);
    return (
      <QueueShell>
        <div
          className="flex flex-col items-center justify-center gap-3 py-8 text-center"
          role="alert"
          data-testid="reviews-due-error"
        >
          <AlertCircle
            className="h-8 w-8 text-destructive"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">{message}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="reviews-due-retry"
          >
            {isFetching ? "Retrying…" : "Try again"}
          </Button>
        </div>
      </QueueShell>
    );
  }

  // Re-assert ascending ordering client-side for deterministic display (Req 2.2).
  const cycles = sortDueCycles(data ?? []);

  // ---- Empty state (Req 2.5) --------------------------------------------
  if (cycles.length === 0) {
    return (
      <QueueShell>
        <div
          className="flex flex-col items-center justify-center gap-2 py-8 text-center"
          data-testid="reviews-due-empty"
        >
          <Inbox className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium">No reviews are due right now.</p>
          <p className="text-sm text-muted-foreground">
            You're all caught up — new reviews will appear here when they're due.
          </p>
        </div>
      </QueueShell>
    );
  }

  // ---- Loaded state (Req 2.3) -------------------------------------------
  return (
    <QueueShell>
      <ul className="space-y-2" data-testid="reviews-due-list">
        {cycles.map((cycle) => {
          const route = unifiedRouteForCycle(cycle.id);
          const urgency = describeUrgency(cycle.dueDate);
          return (
            <li key={cycle.id} data-testid={`reviews-due-item-${cycle.id}`}>
              {/* The whole row is a single click target so the space between
                  the customer name and the action isn't dead — clicking
                  anywhere opens the unified review. */}
              <button
                type="button"
                onClick={() => setLocation(route)}
                aria-label={`Open unified review for ${cycle.customerName}`}
                data-testid={`reviews-due-open-${cycle.id}`}
                className="group flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                    urgency.iconWrapClass,
                  )}
                >
                  <CalendarClock className="h-4 w-4" aria-hidden="true" />
                </span>

                <div className="min-w-0 flex-1">
                  <p
                    className="truncate font-medium"
                    title={cycle.customerName}
                  >
                    {cycle.customerName}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Due <FormattedDate date={cycle.dueDate} format="date" />
                  </p>
                </div>

                <span
                  className={cn(
                    "hidden shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium sm:inline-block",
                    urgency.badgeClass,
                  )}
                >
                  {urgency.label}
                </span>

                <span className="flex shrink-0 items-center text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">
                  <span className="hidden md:inline">Open review</span>
                  <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </QueueShell>
  );
}

export default ReviewsDueQueue;
